import { useEffect, useMemo, useState, useCallback } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Loader2, AlertCircle } from 'lucide-react'
import { useValidityEditStore } from '@/store/validityEditStore'
import { PeriodGroupPanel } from './PeriodGroupPanel'
import { PeriodGroupFilterBar } from './PeriodGroupFilterBar'
import { BatchSizeSlider } from './BatchSizeSlider'
import { ValidityPeriodPicker } from './ValidityPeriodPicker'
import type { PeriodGroup, BulkUpdateValidityResponse } from '@/services/api/timetable'

interface AdjustValidityDialogProps {
  isOpen: boolean
  teacherIds: string[]
  allowReplace?: boolean
  onClose: () => void
  onSuccess: (result: BulkUpdateValidityResponse) => void
}

export function AdjustValidityDialog({
  isOpen,
  teacherIds,
  allowReplace = true,
  onClose,
  onSuccess,
}: AdjustValidityDialogProps) {
  const {
    periodGroups,
    isLoading,
    error,
    isApplying,
    applyProgress,
    selectedKeys,
    expandedGroups,
    filter,
    showOnlySelected,
    batchSize,
    newValidFrom,
    newValidTo,
    mode,
    toggleGroup,
    toggleTeacher,
    selectAllInGroup,
    selectAll,
    deselectAll,
    loadPeriodGroups,
    applyChanges,
    setNewValidity,
    setMode,
    setFilter,
    setShowOnlySelected,
    setBatchSize,
    reset,
  } = useValidityEditStore()

  // Compute derived values locally using useMemo (Zustand getters don't work reliably)
  const selectedCount = useMemo(() => selectedKeys.size, [selectedKeys])
  
  const totalEntryCount = useMemo(() => {
    let count = 0
    selectedKeys.forEach(key => {
      const parts = key.split('|')
      if (parts.length < 3 || !parts[2]) return
      const validFrom = parts[0] === 'null' ? null : parts[0]
      const validTo = parts[1] === 'null' ? null : parts[1]
      const teacherId = parts[2]
      
      const group = periodGroups.find(g => 
        (g.valid_from || 'null') === (validFrom || 'null') && 
        (g.valid_to || 'null') === (validTo || 'null')
      )
      if (group) {
        const teacher = group.teachers.find(t => t.teacher_id === teacherId)
        if (teacher) {
          count += teacher.entry_count
        }
      }
    })
    return count
  }, [selectedKeys, periodGroups])

  const [showConflictWarning, setShowConflictWarning] = useState(false)

  // Load data when dialog opens
  useEffect(() => {
    if (isOpen && teacherIds.length > 0) {
      loadPeriodGroups(teacherIds)
    }
  }, [isOpen, teacherIds, loadPeriodGroups])

  useEffect(() => {
    if (isOpen && !allowReplace && mode === 'replace') setMode('update')
  }, [isOpen, allowReplace, mode, setMode])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  // Filter groups
  const filteredGroups = useMemo(() => {
    let groups = [...periodGroups]

    // Apply status filter
    switch (filter) {
      case 'current':
        groups = groups.filter(g => g.is_current)
        break
      case 'future':
        groups = groups.filter(g => {
          if (!g.valid_from) return false
          return new Date(g.valid_from) > new Date()
        })
        break
      case 'expired':
        groups = groups.filter(g => {
          if (!g.valid_to) return false
          return new Date(g.valid_to) < new Date()
        })
        break
      case 'duplicates':
        // Show groups with teachers appearing in multiple groups
        const teacherGroupCount = new Map<string, number>()
        periodGroups.forEach(gr => {
          gr.teachers.forEach(t => {
            teacherGroupCount.set(t.teacher_id, (teacherGroupCount.get(t.teacher_id) || 0) + 1)
          })
        })
        groups = groups.filter(g => 
          g.teachers.some(t => (teacherGroupCount.get(t.teacher_id) ?? 0) > 1)
        )
        break
    }

    // Show only selected
    if (showOnlySelected) {
      const selectedTeacherIds = new Set<string>()
      selectedKeys.forEach(key => {
        const parts = key.split('|')
        if (parts.length >= 3 && parts[2]) {
          selectedTeacherIds.add(parts[2])
        }
      })
      groups = groups.filter(g => 
        g.teachers.some(t => selectedTeacherIds.has(t.teacher_id))
      )
    }

    return groups
  }, [periodGroups, filter, showOnlySelected, selectedKeys])

  // Check for conflicts
  const checkConflicts = useCallback(() => {
    if (mode !== 'replace') return false
    return selectedCount > 0
  }, [mode, selectedCount])

  // Validate dates
  const [validationError, setValidationError] = useState<string | null>(null)
  
  const validateDates = useCallback(() => {
    if (newValidFrom && newValidTo) {
      const from = new Date(newValidFrom)
      const to = new Date(newValidTo)
      if (isNaN(from.getTime()) || isNaN(to.getTime())) {
        setValidationError('Ungültiges Datumsformat')
        return false
      }
      if (from > to) {
        setValidationError('Startdatum muss vor Enddatum liegen')
        return false
      }
    }
    setValidationError(null)
    return true
  }, [newValidFrom, newValidTo])

  // Handle apply
  const handleApply = async () => {
    // Validate dates first
    if (!validateDates()) {
      return
    }

    if (checkConflicts()) {
      setShowConflictWarning(true)
      return
    }

    const result = await applyChanges()
    
    // Always call onSuccess to refresh the frontend, even if there are errors
    onSuccess({
      updated_count: result.updatedCount,
      affected_teachers: selectedCount,
      change_id: result.changeId,
      expires_at: result.expiresAt,
      errors: result.errors || [],
    })
  }

  // Handle apply with conflict confirmation
  const handleConfirmApply = async () => {
    setShowConflictWarning(false)
    const result = await applyChanges()
    
    // Always call onSuccess to refresh the frontend, even if there are errors
    onSuccess({
      updated_count: result.updatedCount,
      affected_teachers: selectedCount,
      change_id: result.changeId,
      expires_at: result.expiresAt,
      errors: result.errors || [],
    })
  }

  // Helper to make group key
  const makeGroupKey = (validFrom: string | null, validTo: string | null) =>
    `${validFrom || 'null'}|${validTo || 'null'}`

  // Helper to get selected teachers for a group
  const getSelectedTeachersForGroup = (group: PeriodGroup) => {
    const selected = new Set<string>()
    group.teachers.forEach(teacher => {
      const key = makeGroupKey(group.valid_from, group.valid_to) + '|' + teacher.teacher_id
      if (selectedKeys.has(key)) {
        selected.add(teacher.teacher_id)
      }
    })
    return selected
  }

  return (
    <>
      <Modal
        isOpen={isOpen && !showConflictWarning}
        onClose={onClose}
        title={`Datum anpassen ${teacherIds.length > 0 ? `(${teacherIds.length} Lehrer)` : ''}`}
        size="xl"
      >
        <div className="max-h-[80vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
              <span className="text-gray-600">Lade Perioden...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-600">
              <AlertCircle className="h-6 w-6 mr-2" />
              <span>{error}</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Filter Bar */}
              <PeriodGroupFilterBar
                groups={periodGroups}
                activeFilter={filter}
                selectedTeachers={selectedKeys}
                onFilterChange={setFilter}
                showOnlySelected={showOnlySelected}
                onToggleShowOnlySelected={() => setShowOnlySelected(!showOnlySelected)}
              />

              {/* Period Groups List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {filteredGroups.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Keine Perioden gefunden
                  </div>
                ) : (
                  filteredGroups.map((group) => (
                    <PeriodGroupPanel
                      key={makeGroupKey(group.valid_from, group.valid_to)}
                      group={group}
                      isExpanded={expandedGroups.has(makeGroupKey(group.valid_from, group.valid_to))}
                      selectedTeachers={getSelectedTeachersForGroup(group)}
                      onToggleExpand={() => toggleGroup(group.valid_from, group.valid_to)}
                      onSelectAll={(selected) =>
                        selectAllInGroup(group.valid_from, group.valid_to, selected)
                      }
                      onToggleTeacher={(teacherId) =>
                        toggleTeacher(group.valid_from, group.valid_to, teacherId)
                      }
                    />
                  ))
                )}
              </div>

              {/* Selection Summary */}
              <div className="flex items-center justify-between py-2 border-t">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>
                    <span className="font-medium text-gray-900">{selectedCount}</span> Lehrer ausgewählt
                  </span>
                  <span>
                    <span className="font-medium text-gray-900">{totalEntryCount}</span> Einträge
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={deselectAll}>
                    Alle abwählen
                  </Button>
                  <Button variant="secondary" size="sm" onClick={selectAll}>
                    Alle auswählen
                  </Button>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t pt-4 space-y-4">
                {/* Batch Size Slider */}
                <BatchSizeSlider
                  value={batchSize}
                  onChange={setBatchSize}
                  min={1}
                  max={200}
                />

                {/* Validity Picker */}
                <ValidityPeriodPicker
                  validFrom={newValidFrom}
                  validTo={newValidTo}
                  onChange={(from, to) => setNewValidity(from, to)}
                />

                {/* Mode Selection */}
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Modus:</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="update"
                        checked={mode === 'update'}
                        onChange={() => setMode('update')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Update</span>
                      <span className="text-xs text-gray-500">(Einträge behalten)</span>
                    </label>
                    {allowReplace && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="replace"
                        checked={mode === 'replace'}
                        onChange={() => setMode('replace')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Replace</span>
                      <span className="text-xs text-gray-500">(Alte Einträge ersetzen)</span>
                    </label>
                    )}
                  </div>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{validationError}</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  {isApplying && applyProgress && (
                    <span className="text-sm text-blue-600 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {applyProgress}
                    </span>
                  )}
                  <Button variant="secondary" onClick={onClose} disabled={isApplying}>
                    Abbrechen
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={selectedCount === 0 || isApplying}
                  >
                    {isApplying ? 'Speichern...' : 'Übernehmen'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Conflict Warning Modal */}
      <Modal
        isOpen={showConflictWarning}
        onClose={() => setShowConflictWarning(false)}
        title="Konflikt erkannt"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-700">
                Im Replace-Modus werden existierende Einträge mit der neuen Gültigkeit gelöscht.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Sind Sie sicher, dass Sie fortfahren möchten?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowConflictWarning(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleConfirmApply}>
              Trotzdem fortfahren
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
